import { ease1 } from '@/animations/easings';
import { SplitText } from 'gsap/SplitText';
import PageHandlersService from '@/utils/page-handlers-service';
import { DEVICE_WIDTH } from '@/utils/viewport-width';
import gsap from 'gsap';

const pageHandlers = new PageHandlersService();

const handleTextAndGetHeadingElement = (selector: string) => {
    const splitTextEntity = new SplitText(selector, { type: 'lines,words' });

    splitTextEntity.lines.forEach((line) => {
        line.classList.add('animated-text-line');
    });

    splitTextEntity.words.forEach((word) => {
        word.classList.add('a-animated-heading-word');
    });

    return document.querySelector(selector);
};

const defineHeadingLinesAnimation = (selector: string) => {
    const headingElement = handleTextAndGetHeadingElement(selector);

    gsap.to(null, {
        scrollTrigger: {
            trigger: selector,
            start: 'top bottom-=25%',
            onEnter: () => {
                headingElement?.classList.add('entered');
            },
        },
    });
};

const init = () => {
    const isNotMobile = !DEVICE_WIDTH.isMobile;

    const themeTriggers = document.querySelectorAll<HTMLElement>('.js-theme-trigger');

    // Цвет фона
    themeTriggers.forEach((themeTrigger, elementIndex) => {
        gsap.to(document.documentElement, {
            scrollTrigger: {
                trigger: themeTrigger,
                start: 'top top',
                onEnter: () => {
                    if (themeTrigger.dataset.bgColor) {
                        document.body.style.setProperty('--current-bg-color', themeTrigger.dataset.bgColor);
                    }

                    if (themeTrigger.dataset.textColor) {
                        document.body.style.setProperty('--current-text-color', themeTrigger.dataset.textColor);
                    }
                },
                onLeaveBack: () => {
                    document.body.style.setProperty(
                        '--current-bg-color',
                        themeTriggers[elementIndex - 1]?.dataset.bgColor ?? '#141414',
                    );

                    document.body.style.setProperty(
                        '--current-text-color',
                        themeTriggers[elementIndex - 1]?.dataset.textColor ?? '#fff',
                    );
                },
            },
        });
    });

    const localParallaxItems = document.querySelectorAll('.js-a-content-parallax-item');

    localParallaxItems.forEach((item) => {
        gsap.fromTo(
            item,
            {
                y: '8vmax',
            },
            {
                y: '-8vmax',
                ease: 'linear',
                scrollTrigger: {
                    trigger: item,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: isNotMobile ? 1 : 0,
                },
            },
        );
    });

    const labelElements = document.querySelectorAll('.js-a-content-label');

    labelElements.forEach((element) => {
        element.classList.add('a-animated-heading-word');

        gsap.to(null, {
            scrollTrigger: {
                trigger: element,
                start: 'top bottom-=25%',
                onEnter: () => {
                    element.classList.add('entered');
                },
            },
        });
    });

    const headingElementSelectors = [
        '.js-content-1-heading',
        '.js-content-2-heading',
        '.js-a-content-3-heading',
        '.js-a-content-4-thesis',
        '.js-a-content-5-text',
        '.js-a-content-6-thesis',
        '.js-a-content-7-thesis',
        '.js-a-content-8-heading',
        '.js-a-content-9-heading',
        '.js-a-content-12-thesis',
        '.js-a-content-13-heading',
        '.js-a-content-13-thesis',
        '.js-a-content-13-bottom-thesis',
        '.js-a-content-14-thesis',
        '.js-a-content-14-conclusion',
    ];

    headingElementSelectors.forEach((selector) => {
        defineHeadingLinesAnimation(selector);
    });

    const bottomAppearanceElements = document.querySelectorAll('.js-a-content-bottom-appearance');

    bottomAppearanceElements.forEach((element) => {
        gsap.fromTo(
            element,
            {
                opacity: 0,
                y: '8vmax',
            },
            {
                opacity: 1,
                y: '0vmax',
                ease: ease1,
                duration: 1,
                scrollTrigger: {
                    trigger: element,
                    start: 'top bottom-=25%',
                },
            },
        );
    });

    gsap.to(document.documentElement, {
        scrollTrigger: {
            trigger: '.js-content-3-container',
            onEnter: () => {
                gsap.set('.js-content-2-img', { opacity: 0 });
            },
            onLeaveBack: () => {
                gsap.set('.js-content-2-img', { opacity: 1 });
            },
        },
    });

    // Hero блок
    const firstViewportTimeline = gsap.timeline({ defaults: { duration: 1, ease: ease1 } });

    const heroBlockBg = document.querySelector('.js-hero-bg');

    heroBlockBg?.classList.add('entered');

    firstViewportTimeline.delay(0.1);

    firstViewportTimeline.set('.js-hero-heading', {
        opacity: 1,
    });

    const splitTextEntity = new SplitText('.js-hero-heading', { type: 'lines,words' });

    const heroHeadingLines: Element[] = [];

    splitTextEntity.lines.forEach((line) => {
        line.classList.add('animated-text-line');

        const lineWords = Array.from(line.children);

        lineWords.forEach((word) => {
            word.classList.add('a-animated-heading-word');
        });

        heroHeadingLines.push(line);
    });

    heroHeadingLines.forEach((line, lineIndex) => {
        firstViewportTimeline.to(
            null,
            {
                duration: 0.9,
                onStart: () => {
                    line.classList.add('entered');
                },
            },
            lineIndex === 0 ? 0 : '-=0.5',
        );
    });

    firstViewportTimeline.fromTo(
        '.js-hero-subscription',
        {
            opacity: 0,
            yPercent: 100,
        },
        {
            opacity: 1,
            yPercent: 0,
        },
        '-=0.1',
    );

    pageHandlers.set(
        window,
        () => {
            if (window.scrollY <= 0) {
                gsap.set('.js-content-1-heading', { opacity: 0 });
            } else {
                gsap.set('.js-content-1-heading', { opacity: 1 });
            }
        },
        'scroll',
    );

    gsap.fromTo(
        '.js-hero-content',
        {
            opacity: 1,
        },
        {
            opacity: 0,
            ease: ease1,
            scrollTrigger: {
                trigger: '.js-hero-container',
                start: 'top top',
                end: 'bottom top+=20%',
                scrub: true,
            },
        },
    );

    // Значительный вклад в становление... (a-content-2)

    gsap.to(document.documentElement, {
        scrollTrigger: {
            trigger: '.js-content-2-container',
            end: 'bottom bottom',
            onLeave: () => {
                gsap.set('.js-content-2-container', { opacity: 0 });
            },
            onEnterBack: () => {
                gsap.set('.js-content-2-container', { opacity: 1 });
            },
        },
    });

    const sutraCircle = document.querySelector('.js-a-c-10-circle');

    gsap.fromTo(
        sutraCircle,
        { rotate: 0 },
        {
            rotate: 180,
            scrollTrigger: {
                trigger: sutraCircle,
                start: 'top bottom',
                end: 'bottom top',
                scrub: DEVICE_WIDTH.isDesktop ? 2 : true,
            },
        },
    );

    const sutraCard = document.querySelector('.js-a-10-card');

    gsap.fromTo(
        sutraCard,
        {
            y: '30vh',
        },
        {
            y: '0vh',
            ease: ease1,
            duration: 1,
            scrollTrigger: {
                trigger: sutraCard,
                start: 'top bottom-=25vh',
            },
        },
    );

    gsap.fromTo(
        '.js-a-content-11-item-1',
        {
            y: '6vmax',
        },
        {
            y: '-6vmax',
            ease: 'linear',
            scrollTrigger: {
                trigger: '.js-a-content-11-item-1',
                start: 'top bottom',
                end: 'bottom top',
                scrub: isNotMobile ? 1 : 0,
            },
        },
    );

    gsap.fromTo(
        '.js-a-content-11-item-2',
        {
            y: '23vmax',
        },
        {
            y: '-23vmax',
            ease: 'linear',
            scrollTrigger: {
                trigger: '.js-a-content-11-item-1',
                start: 'top bottom',
                end: 'bottom top',
                scrub: isNotMobile ? 1 : 0,
            },
        },
    );
};

const destroy = () => {
    pageHandlers.clear();
};

const _module = { init, destroy };

export default _module;
